import { BANK_FX_SOURCE, type Occurrence, type PaymentKind } from '@klopotun/contracts';
import {
  formatMoney,
  graceDaysFor,
  occurrenceDisplayStatus,
  parseCivilDate,
  type CivilDate,
  type OccurrenceDisplayStatus,
} from '@klopotun/domain';
import { VARIABLE_AMOUNT_TEXT } from '../../payment-view/index.js';
import type { OccurrenceMark } from '../model/occurrence-mark.js';

const MARK_TEXT: Record<OccurrenceDisplayStatus, string> = {
  upcoming: 'попереду',
  due_today: 'час платити',
  overdue: 'прострочено',
  paid: 'оплачено',
  paused: 'на паузі',
  cancelled: 'не було платежу',
};

const BANK_WAIT_MARK: OccurrenceMark = { label: 'чекаємо банк', tone: 'muted' };

const WAITS_FOR_BANK: OccurrenceDisplayStatus[] = ['due_today', 'overdue'];

export type OccurrenceMarkInput = {
  occurrence: Occurrence;
  kind: PaymentKind;
  today: CivilDate;
  autoGraceDays: number;
  isFromBank: boolean;
};

export const occurrenceMarkOf = (input: OccurrenceMarkInput): OccurrenceMark => {
  const displayStatus = occurrenceDisplayStatus({
    status: input.occurrence.status,
    dueDate: parseCivilDate(input.occurrence.dueDate),
    today: input.today,
    graceDays: graceDaysFor(input.kind, input.autoGraceDays),
  });
  const isWaitingForBank = input.isFromBank && WAITS_FOR_BANK.includes(displayStatus);

  if (isWaitingForBank) {
    return BANK_WAIT_MARK;
  }

  return {
    label: MARK_TEXT[displayStatus],
    tone: displayStatus === 'overdue' ? 'danger' : 'muted',
  };
};

const formatPaidAmount = (occurrence: Occurrence): string | null => {
  if (occurrence.status !== 'paid') {
    return null;
  }

  const { paidAmountMinor, paidCurrency } = occurrence;

  if (paidAmountMinor === null || paidCurrency === null) {
    return null;
  }

  return formatMoney({ amountMinor: paidAmountMinor, currency: paidCurrency });
};

/**
 * An empty expected amount belongs to a payment with a variable amount (design note C5a): it is a
 * fact of its own, not a number that failed to arrive.
 */
export const formatExpectedAmount = (
  occurrence: Occurrence,
  variableAmountText = VARIABLE_AMOUNT_TEXT,
): string => {
  if (occurrence.expectedAmountMinor === null) {
    return variableAmountText;
  }

  return formatMoney({
    amountMinor: occurrence.expectedAmountMinor,
    currency: occurrence.expectedCurrency,
  });
};

/**
 * What a period was expected to cost and what was actually paid for it are two different facts,
 * and a confirmed period is asked about the second one.
 */
export const formatOccurrenceAmount = (
  occurrence: Occurrence,
  variableAmountText = VARIABLE_AMOUNT_TEXT,
): string | null => {
  const hasNoPayment = occurrence.status === 'cancelled';

  if (hasNoPayment) {
    return null;
  }

  return formatPaidAmount(occurrence) ?? formatExpectedAmount(occurrence, variableAmountText);
};

/**
 * What a confirmed payment came to in the currency the totals are kept in, or nothing at all when
 * there is nothing to add: an unconfirmed period, a payment already in that currency, or a rate
 * that could not be had on the day.
 *
 * The currency comes from the period rather than from the settings, which is the whole reason it
 * is stored per row (design note C6): an old period keeps saying what it meant even after the main
 * currency changes.
 */
export const formatBaseAmount = (occurrence: Occurrence): string | null => {
  const isConfirmed = occurrence.status === 'paid';
  const hasConversion =
    isConfirmed && occurrence.baseAmountMinor !== null && occurrence.fxBaseCurrency !== null;
  const saysSomethingNew = hasConversion && occurrence.fxBaseCurrency !== occurrence.paidCurrency;

  if (!saysSomethingNew || occurrence.baseAmountMinor === null) {
    return null;
  }

  const baseAmount = formatMoney({
    amountMinor: occurrence.baseAmountMinor,
    currency: occurrence.fxBaseCurrency ?? '',
  });
  const isBankAmount = occurrence.fxSource === BANK_FX_SOURCE;

  return isBankAmount ? baseAmount : `≈ ${baseAmount}`;
};
